import { Component, EventEmitter, HostBinding, HostListener, Input, Output } from '@angular/core';
import { IconComponent } from 'components/layout/icon/icon.component';
import { InputStateDirective } from 'directives/input-state.directive';

@Component({
	selector: 'drag-uploader',
	template:
		'@if (loadMessage) { <icon small type="hourglass-half" />{{ loadMessage }} }' +
		'@else { <icon small type="upload" />{{ label }} }' +
		'<input type="file" (change)="onChange($event.currentTarget)" accept="{{ accept }}" [attr.multiple]="multiple ? \'1\' : null">',
	styleUrls: ['./drag-uploader.scss'],
	imports: [InputStateDirective, IconComponent]
})
export class DragUploaderComponent {

	@Input() accept = '';
	@Input() label = '';
	@Input() loadMessage?: string;
	@Input() @HostBinding('class.disabled') disabled = false;
	@Input() multiple = false;
	@HostBinding('class.dragging') dragging = false;

	@Output() upload = new EventEmitter<{ file: File, name: string; }[]>();

	public onChange(input: EventTarget | null) {
		if (!(input instanceof HTMLInputElement)) return;
		if (input.files) this.upload.emit(Array.from(input.files).map((file) => { return { file, name: file.name }; }));
		input.value = '';
	}

	private getFile(entry: FileSystemFileEntry) {
		return new Promise<File>((resolve, reject) => {
			entry.file(resolve, reject);
		});
	}
	private async getDirectoryContents(entry: FileSystemDirectoryEntry) {
		const reader = entry.createReader();
		const entries: FileSystemEntry[] = [];
		while (true) {
			const readEntries = await new Promise<FileSystemEntry[]>((resolve) => {
				reader.readEntries(resolve);
			});
			if (readEntries.length === 0) break;
			entries.push(...readEntries);
		}
		return entries;
	}

	@HostListener('drop', ['$event'])
	async onDrop(event: DragEvent) {

		event.preventDefault();
		if (!event.dataTransfer || !event.dataTransfer.items) return;

		const uploadedFiles: { file: File, name: string; }[] = [];
		const draggedEntries = Array.from(event.dataTransfer.items).map(i => i.webkitGetAsEntry());
		const handleEntries = async (entries: (FileSystemEntry | null)[]) => {

			for (const entry of entries) {
				if (!entry) continue;
				if (entry.isFile) {

					const name = entry.fullPath.replace(/\\/g, '/').replace(/^\//, ''); // Convert all directory delimiters to forward slash and remove potential leading slash
					const file = await this.getFile(entry as FileSystemFileEntry);
					uploadedFiles.push({ file, name });

				}
				else if (entry.isDirectory) {
					await handleEntries(await this.getDirectoryContents(entry as FileSystemDirectoryEntry));
				}
			}

		};
		await handleEntries(draggedEntries);
		this.upload.emit(uploadedFiles);

		this.dragging = false;
	}

	@HostListener('dragleave')
	onDragLeave() {
		this.dragging = false;
	}

	@HostListener('dragover')
	onDragOver() {
		this.dragging = true;
	}
}
